// src/features/steps/components/EditConfigScreen.jsx
import React from 'react';
import EditDurationForm from './EditDurationForm';
import { useProjectsContext } from '../../../projects/hooks/useProjectsContext';
import EditSequentialPhasesForm from './EditSequentialPhasesForm';
import EditEvenDistributionForm from './EditEvenDistributionForm';
import { getShapingDisplay } from '../../../../shared/utils/stepDisplayUtils';
import EditMarkerPhasesForm from './EditMarkerPhasesForm';

const EditConfigScreen = ({
    componentIndex,
    editingStepIndex,
    onBack
}) => {
    const { currentProject } = useProjectsContext();

    // Component validation
    if (!currentProject || componentIndex === null || !currentProject.components[componentIndex]) {
        return (
            <div className="min-h-screen bg-yarn-50 flex items-center justify-center">
                <div className="text-center bg-white rounded-xl p-6 shadow-lg border-2 border-wool-200">
                    <div className="text-4xl mb-4">❌</div>
                    <h3 className="text-lg font-medium text-wool-600 mb-2">Component not found</h3>
                    <button
                        onClick={onBack}
                        className="btn-primary btn-sm"
                    >
                        ← Back
                    </button>
                </div>
            </div>
        );
    }

    const component = currentProject.components[componentIndex];
    const step = component.steps[editingStepIndex];

    if (!step) {
        return (
            <div className="min-h-screen bg-yarn-50 flex items-center justify-center">
                <div className="text-center bg-white rounded-xl p-6 shadow-lg border-2 border-wool-200">
                    <div className="text-4xl mb-4">❌</div>
                    <h3 className="text-lg font-medium text-wool-600 mb-2">Step not found</h3>
                    <button
                        onClick={onBack}
                        className="btn-primary btn-sm"
                    >
                        ← Back
                    </button>
                </div>
            </div>
        );
    }

    // Determine configuration type from step data
    const getConfigType = () => {
        // Check if step has shaping using utility
        const shapingInfo = getShapingDisplay(step);

        if (shapingInfo) {
            // Use the same reliable shaping detection logic as everywhere else
            const shapingConfig = step.wizardConfig?.shapingConfig || step.advancedWizardConfig?.shapingConfig;
            const shapingType = shapingConfig?.type;

            if (shapingType === 'even_distribution') {
                return 'even_distribution';
            } else if (shapingType === 'phases') {
                return 'sequential_phases';
            } else if (shapingType === 'marker_phases') {
                return 'marker_phases';
            }

            // Legacy shaping detection - assume even distribution for legacy
            if (shapingConfig && Object.keys(shapingConfig).length > 0) {
                return 'even_distribution';
            }
        }

        // No shaping = duration configuration
        return 'duration';
    };

    const configType = getConfigType();

    // Render appropriate configuration component
    switch (configType) {
        case 'duration':
            return (
                <EditDurationForm
                    componentIndex={componentIndex}
                    editingStepIndex={editingStepIndex}
                    onBack={onBack}
                />
            );

        case 'even_distribution':
            return (
                <EditEvenDistributionForm
                    componentIndex={componentIndex}
                    editingStepIndex={editingStepIndex}
                    onBack={onBack}
                />
            );

        case 'sequential_phases':
            return (
                <EditSequentialPhasesForm
                    componentIndex={componentIndex}
                    editingStepIndex={editingStepIndex}
                    onBack={onBack}
                />
            );

        case 'marker_phases':
            return (
                <EditMarkerPhasesForm
                    componentIndex={componentIndex}
                    editingStepIndex={editingStepIndex}
                    onBack={onBack}
                />
            );

        default:
            return (
                <div className="min-h-screen bg-yarn-50 flex items-center justify-center">
                    <div className="text-center bg-white rounded-xl p-6 shadow-lg border-2 border-wool-200">
                        <div className="text-4xl mb-4">❓</div>
                        <h3 className="text-lg font-medium text-wool-600 mb-2">Unknown configuration type</h3>
                        <p className="text-sm text-wool-500 mb-4">This step's configuration cannot be edited.</p>
                        <button
                            onClick={onBack}
                            className="btn-primary btn-sm"
                        >
                            ← Back
                        </button>
                    </div>
                </div>
            );
    }
};

export default EditConfigScreen;